import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getGroupList, deleteGroup } from "../taskSaga";
import { componentKey } from "../taskSlice";
import WrapperCard from "../../../component-lib/Atoms/card/EventEHRWrapperCard";
import Table from "../../../component-lib/Organisms/table/EventEHRTable";
import Button from "../../../component-lib/Molecules/button/Button";
import { TEXT_CONSTANTS } from "../../../utils/text-constants/textConstants";
import CreateGroupDialog from "./dialogs/CreateGroupDialog";
import ViewGroupDialog from "./dialogs/ViewGroupDialog";
import EventEHRConfirmationDialog from "../../../component-lib/Molecules/confimation-dialog/EventEHRConfirmationDialog";
import TooltipForTable from "../../../component-lib/Molecules/tooltip/TooltipForTable";
import MenuDropdown from "../../../component-lib/Molecules/menuDropdown/MenuDropdown";
import moment from "moment";

interface GroupsScreenProps {
  searchKey?: string;
}

const GroupsScreen: React.FC<GroupsScreenProps> = ({ searchKey = "" }) => {
  const dispatch = useDispatch();
  const { groupListData, closeCreateGroupDialog } = useSelector((state: any) => state[componentKey]);
  
  const [pageNumber, setPageNumber] = useState(1);
  const [rowPerPage, setRowPerPage] = useState("10");
  const [openCreateGroupDialog, setOpenCreateGroupDialog] = useState(false);
  const [openViewGroupDialog, setOpenViewGroupDialog] = useState(false);
  const [openConfirmationDialog, setOpenConfirmationDialog] = useState(false);
  const [defaultGroupData, setDefaultGroupData] = useState<any>(null);
  const [selectedGroup, setSelectedGroup] = useState<any>(null);
  const [groupToDelete, setGroupToDelete] = useState<any>(null);

  // Table columns configuration
  const groupColumns = [
    {
      field: "name",
      label: <div>Group Name</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.name || "-"}
          </p>
        );
      },
    },
    {
      field: "members",
      label: <div>Members</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            <TooltipForTable 
              label={row?.memberCount > 3 ? row?.membersList : row?.members}
            />
          </p>
        );
      },
    },
    {
      field: "createdBy",
      label: <div>Created By</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.createdBy || "-"}
          </p>
        );
      },
    },
    {
      field: "createdAt",
      label: <div>Created Date</div>,
      renderLogic: (row: any) => {
        return (
          <p className="text-neutral-90 font-normal">
            {row?.createdAt || "-"}
          </p>
        );
      },
    },
    {
      field: "action",
      label: <div>{TEXT_CONSTANTS?.ACTION}</div>,
      renderLogic: (row: any) => {
        return (
          <>
            <div className="flex items-center">
              <MenuDropdown
                options={[
                  {
                    label: "View Group",
                    onClickCb: () => {
                      setSelectedGroup(row?.rowData);
                      setOpenViewGroupDialog(true);
                    },
                  },
                  {
                    label: "Edit Group",
                    onClickCb: () => {
                      handleEditGroup(row?.rowData);
                    },
                  },
                  {
                    label: "Delete Group",
                    onClickCb: () => {
                      handleDeleteGroup(row?.rowData);
                    },
                  },
                ]}
              />
            </div>
          </>
        );
      },
    },
  ];

  // Transform group data for table
  const groupRowData = groupListData?.taskGroups?.map((data: any) => {
    // Create members list with tooltip
    const membersList =
      data?.memebers
        ?.map((member: any) => {
          const person = member?.users || member?.adminUser;
          return `${person?.firstName || ""} ${person?.lastName || ""}`.trim();
        })
        .filter(Boolean)
        .join(", ") || "No members";
    
    const memberCount = data?._count?.memebers || 0;
    const membersDisplay = memberCount > 3 
      ? `${memberCount} members` 
      : membersList;

    return {
      name: data?.name || "-",
      members: membersDisplay,
      createdBy: `${data?.creator?.firstName || "-"} ${data?.creator?.lastName || ""}`,
      createdAt: data?.createdAt
        ? // createdAt is a true instant — show in local time (matches the
          // viewer's clock; UTC would shift a day for evening records).
          moment(data?.createdAt).format("MM/DD/YYYY")
        : "-",
      rowData: data,
      // Store full members list for tooltip
      membersList: membersList,
      memberCount: memberCount,
    };
  }) || [];

  // Handle edit group
  const handleEditGroup = (rowData: any) => {
    // Map members to the format expected by the dialog
    const mappedUsers =
      rowData?.memebers
        ?.filter((member: any) => member?.userId || member?.adminUserId)
        .map((member: any) => {
          const person = member?.users || member?.adminUser;
          return {
            label: `${person?.firstName || ""} ${person?.lastName || ""}`.trim(),
            value: member?.userId || member?.adminUserId,
          };
        }) || [];
    
    setDefaultGroupData({
      id: rowData?.id,
      name: rowData?.name,
      users: mappedUsers,
    });
    setOpenCreateGroupDialog(true);
  };

  // Handle create group
  const handleCreateGroup = () => {
    setDefaultGroupData(null);
    setOpenCreateGroupDialog(true);
  };

  // Handle close dialog
  const handleCloseDialog = () => {
    setOpenCreateGroupDialog(false);
    setDefaultGroupData(null);
  };

  // Handle group creation/update
  const handleGroupSubmit = (formData: any) => {
    // This will be handled by the dialog's form submission
    console.log("Group submitted:", formData);
  };

  // Handle delete group
  const handleDeleteGroup = (groupData: any) => {
    setGroupToDelete(groupData);
    setOpenConfirmationDialog(true);
  };

  // Handle confirmation dialog close
  const handleConfirmationClose = (action: string) => {
    if (action === "submit" && groupToDelete) {
      dispatch(deleteGroup(groupToDelete.id));
    }
    setOpenConfirmationDialog(false);
    setGroupToDelete(null);
  };

  // Load groups data
  useEffect(() => {
    const payload = {
      pageNumber: pageNumber,
      limit: rowPerPage,
      searchKey: searchKey,
    };
    dispatch(getGroupList(payload));
  }, [pageNumber, rowPerPage, searchKey]);

  // Refresh data when dialog closes (after create/update)
  useEffect(() => {
    if (closeCreateGroupDialog) {
      const payload = {
        pageNumber: pageNumber,
        limit: rowPerPage,
      };
      dispatch(getGroupList(payload));
    }
  }, [closeCreateGroupDialog]);

  return (
    <WrapperCard>
      {/* <div className="flex justify-between items-center mb-4">
        <h1 className="text-2xl font-semibold">Groups Management</h1>
        <Button
          label="Create Group"
          variant="contained"
          onClickCb={handleCreateGroup}
          style={{
            backgroundColor: "#1976d2",
            color: "white",
            padding: "8px 16px",
          }}
        />
      </div> */}

      <Table
        columns={groupColumns}
        rows={groupRowData}
        paginationProps={{
          isPagination: true,
          page: pageNumber,
          totalPages: groupListData?.totalPages || 1,
          totalCount: groupListData?.total || 0,
          limit: rowPerPage,
          rowsPerPageOptions: [10, 25, 50],
          onPageChange: (page: any) => {
            setPageNumber(page);
          },
          handleChangeRowsPerPage: (rowPerPage: any) => {
            setRowPerPage(rowPerPage);
          },
        }}
      />

      <CreateGroupDialog
        isOpen={openCreateGroupDialog}
        handleClose={handleCloseDialog}
        handleCreateGroup={handleGroupSubmit}
        defaultGroupData={defaultGroupData}
      />

      {/* View Group Dialog */}
      {openViewGroupDialog && (
        <ViewGroupDialog
          isOpen={openViewGroupDialog}
          groupData={selectedGroup}
          handleClose={() => setOpenViewGroupDialog(false)}
        />
      )}

      {/* Confirmation Dialog */}
      <EventEHRConfirmationDialog
        open={openConfirmationDialog}
        alertTitle="Delete Group"
        alertDescription={`Are you sure you want to delete the group "${groupToDelete?.name}"? This action cannot be undone.`}
        submitButtonTitle="Delete"
        cancelButtonTitle="Cancel"
        handleClose={handleConfirmationClose}
      />
    </WrapperCard>
  );
};

export default GroupsScreen;
